import { Input as AntInput, InputProps } from 'antd'
import { ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { capitalizeName, getGuiId } from 'utils'
import { IS_NUMBER } from 'utils/regularExpressions'

const StyledInput = styled(AntInput)<{ inputcolor?: string }>`
  height: 36px;
  color: ${({ inputcolor }) => inputcolor || 'inherit'};
  &.non-border:focus {
    border: none;
    border-bottom: 1px solid var(--color-primary-109);
    border-radius: 0;
    box-shadow: none;
  }
  &.non-border {
    border: none;
    border-bottom: 1px solid var(--color-gray-103);
    border-radius: 0;
    padding: 8px 0;
  }
  &.prevent-edit {
    color: inherit;
    background-color: inherit;
    caret-color: transparent;
    cursor: not-allowed;
  }
  &.prevent-edit.cursor-pointer {
    cursor: pointer;
  }
  &.ant-input-affix-wrapper {
    box-shadow: none;
    height: 44px;
    position: relative;
    &:hover {
      border-bottom: 1px solid var(--color-gray-103) !important;
    }
    .ant-input-suffix {
      position: absolute;
      right: 0;
      bottom: 6px;
    }
    .ant-input {
      padding-right: 50px;
    }
  }
  .non-border {
    border: none;
    border-bottom: 1px solid var(--color-gray-103) !important;
    border-radius: 0;
    padding: 8px 0;
  }
  .non-border:focus {
    border: none;
    border-bottom: 1px solid var(--color-primary-109) !important;
    border-radius: 0;
    box-shadow: none;
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const StyledLabel = styled.label`
  color: var(--color-gray-111);
  font-weight: 600;
  font-size: 1.4rem;
  span {
    color: var(--color-red-107);
  }
`

const StyledMaxLength = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  color: var(--color-gray-104);
`
interface IProps extends InputProps {
  title?: string
  children?: ReactNode
  placeholder?: string
  required?: boolean
  preventInput?: boolean
  hasBorder?: boolean
  color?: string
  value?: string | number
  isCapitalizeName?: boolean
  showMaxLength?: boolean
  inputRef?: any
  onKeyDown?: (event: any) => void
}

const Input = (props: IProps) => {
  const { t } = useTranslation()
  const idInput = useRef(getGuiId())
  const {
    title,
    value,
    required,
    placeholder,
    preventInput,
    hasBorder,
    onClick,
    isCapitalizeName,
    onChange,
    type,
    maxLength,
    showMaxLength,
    color,
    onKeyDown,
    inputRef,
    ...resPros
  } = props

  const valueRef = useRef<any>(value)
  const preventInputRef = useRef<any>()

  const handleOnChange = (e: any) => {
    if (preventInputRef.current) return
    if (type === 'number' && !!e?.target?.value) {
      const isNumber = IS_NUMBER.test(e.target.value)
      if (!isNumber) {
        e.preventDefault()
        return
      }
    }
    if (props.maxLength && e.target.value?.length > props.maxLength) {
      e.preventDefault()
      return
    }
    const newValue = e.target.value
    if (onChange) onChange(newValue)
  }

  useEffect(() => {
    const handleOnBlur = () => {
      if (onChange) {
        onChange(capitalizeName(valueRef.current as string).trimStart() as any)
      }
    }
    const element = document.getElementById(idInput.current)
    if (isCapitalizeName) {
      element?.addEventListener('blur', handleOnBlur)
    }
    return () => {
      element?.removeEventListener('blur', handleOnBlur)
    }
  }, [isCapitalizeName])

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    const handleOnKeyDown = (e: any) => {
      if (onKeyDown) onKeyDown(e)
      const isArrowKey =
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e?.key === 'Backspace'
      if (isArrowKey) {
        preventInputRef.current = false
        return
      }
      if (maxLength && valueRef.current?.length >= maxLength) {
        e.preventDefault()
        preventInputRef.current = true
        return
      }
      preventInputRef.current = false
    }
    const element = document.getElementById(idInput.current)
    element?.addEventListener('keydown', handleOnKeyDown)
    return () => {
      element?.removeEventListener('keydown', handleOnKeyDown)
    }
  }, [])

  return (
    <>
      <StyledLabel className="input-label">
        {t(title || '')} {required && <span>*</span>}
      </StyledLabel>
      <StyledInput
        ref={inputRef}
        type={type}
        inputcolor={color}
        className={`${preventInput ? 'prevent-edit' : ''} ${
          hasBorder ? '' : 'non-border'
        } ${!!onClick ? 'cursor-pointer' : ''}`}
        placeholder={t(placeholder || '')}
        disabled={preventInput && !onClick}
        value={value || ''}
        onChange={handleOnChange}
        onClick={onClick}
        maxLength={maxLength}
        {...resPros}
        id={idInput.current}
      />
      {showMaxLength && (
        <StyledMaxLength>
          {value?.toString()?.length || 0}/{maxLength}
        </StyledMaxLength>
      )}
    </>
  )
}

export default Input
